import { createAction } from "@reduxjs/toolkit";
import {
  ApiCall,
  ApiCallCreateRequest,
  Chart,
  ChartCreateRequest,
  Dash,
  DashCreateRequest,
  DataTable,
  DataTableCreateColumnRequest,
  DataTableCreateRequest,
  DataTableCreateRowRequest,
  Navigation,
  NavigationCreateRequest,
  Parameter,
  ParameterCreateRequest,
} from "../types/Dash";
import {
  Organization,
  User,
  Workspace,
  WorkspaceCreateRequest,
} from "../types/User";

import {
  Folder,
  FolderCreateRequest,
} from "../types/Folder";

import {
  Role,
  RoleCreateRequest,
} from "../types/Role";

import {
  Host,
  HostCreateRequest,
  HostPasswordResetRequest,
  HostUpdateRequest,
} from "../types/Host";

import {
  FactotumCreateRequest,
  FactotumUpdateRequest,
} from "../types/Factotum";

import {
  ProjectCreateRequest,
  ProjectUpdateRequest,
} from "../types/Project";
import {
  ProjectTaskCreateRequest,
  ProjectTaskInputCreateRequest,
  ProjectTaskUpdateRequest,
} from "../types/ProjectTask";

import {
  FactotumStateCreateRequest,
  FactotumStateUpdateRequest,
} from "../types/FactotumState";

import {
  FactotumStateTieCreateRequest,
} from "../types/FactotumStateTie";

import {
  Automation,
  AutomationCreateRequest,
  AutomationUpdateRequest,
} from "../types/Automation";

import {
  AutoLine,
  AutoLineCreateRequest,
  AutoLineUpdateRequest,
} from "../types/AutoLine";

import {
  Repo,
  RepoCreateRequest,
} from "../types/Repo";

import {
  ScenarioCreateRequest,
  ScenarioUpdateRequest,
} from "../types/Scenario";

import {
  Bookmark,
  BookmarkCreateRequest,
} from "../types/Bookmark";

export const login = createAction<{ email: string; pass: string }>("login");
export const reset = createAction<{ email: string; }>("reset");
export const finalizeReset = createAction<{ password: string; token: string}>("finalizeReset");

export const changePassword = createAction<{ email:string; password: string}>("changePassword");


// Organizations
export const getOrganizations = createAction("getOrganizations");
export const setOrganization = createAction<Organization>("setOrganization");
export const getOrganizationUsers = createAction("getOrganizationUsers");

// Workspaces
export const getWorkspace = createAction<number>("getWorkspace");
export const getWorkspaces = createAction("getWorkspaces");
export const getWorkspaceSummary = createAction("getWorkspaceSummary");
export const getWorkspaceSummarySearch = createAction<string>("getWorkspaceSummarySearch");
export const setWorkspace = createAction<Workspace>("setWorkspace");
export const deleteWorkspace = createAction<Workspace>("deleteWorkspace");
export const createWorkspace =
  createAction<WorkspaceCreateRequest>("createWorkspace");
export const attachUser = createAction<{
  workspace: Workspace;
  user: User;
  role: string;
}>("attachUser");
export const createUser = createAction<{
  workspace: Workspace;
  email: string;
  password: string;
}>("createUser");

export const createUserInOrg = createAction<{
  email: string;
  password: string;
  workspaces: Array<{id: number, role: string}>;
}>("createUserInOrg");

export const createUserWithOrg = createAction<{
  email: string;
  organization_name: string;
}>("createUserWithOrg");

export const detachUser = createAction<{ workspace: Workspace; user: User }>(
  "detachUser"
);
export const changeUserRole = createAction<{ role:string; user_id: number; workspace: Workspace | undefined }>(
  "changeUserRole"
);

// Folders
export const createFolder =
  createAction<FolderCreateRequest>("createFolder");
export const getFolders = createAction("getFolders");
export const changeFolder = createAction<{id:number, name:string}>("changeFolder");
export const backFolder = createAction("backFolder");

// Roles
export const createRole =
  createAction<RoleCreateRequest>("createRole");
export const createHost =
  createAction<HostCreateRequest>("createHost");
export const updateHost =
  createAction<{ hostId: number; hostUpdateRequest: HostUpdateRequest }>("updateHost");
export const resetHostPassword =
  createAction<{ hostId: number; hostPasswordResetRequest: HostPasswordResetRequest }>("resetHostPassword");
export const createAutomation =
  createAction<AutomationCreateRequest>("createAutomation");
export const updateAutomation =
  createAction<{ automationId: number; automationUpdateRequest: AutomationUpdateRequest }>("updateAutomation");
export const createAutoLine =
  createAction<AutoLineCreateRequest>("createAutoLine");
export const getRoles = createAction("getRoles");
export const getHosts = createAction("getHosts");
export const getFactotums = createAction("getFactotums");
export const createFactotum =
  createAction<FactotumCreateRequest>("createFactotum");
export const updateFactotum =
  createAction<{ factotumId: number; factotumUpdateRequest: FactotumUpdateRequest }>("updateFactotum");
export const getFactotumStates = createAction<number>("getFactotumStates");
export const createFactotumState =
  createAction<{ factotumId: number; factotumStateCreateRequest: FactotumStateCreateRequest }>("createFactotumState");
export const updateFactotumState =
  createAction<{ factotumId: number; stateId: number; factotumStateUpdateRequest: FactotumStateUpdateRequest }>("updateFactotumState");
export const getFactotumStateTies = createAction<number>("getFactotumStateTies");
export const createFactotumStateTie =
  createAction<{ factotumId: number; factotumStateTieCreateRequest: FactotumStateTieCreateRequest }>("createFactotumStateTie");
export const getProjects = createAction("getProjects");
export const getProjectTasks = createAction<number>("getProjectTasks");
export const createProjectTask =
  createAction<{ projectId: number; projectTaskCreateRequest: ProjectTaskCreateRequest }>("createProjectTask");
export const getProjectTask =
  createAction<{ projectId: number; taskId: number }>("getProjectTask");
export const updateProjectTask =
  createAction<{ projectId: number; taskId: number; projectTaskUpdateRequest: ProjectTaskUpdateRequest }>("updateProjectTask");
export const createProjectTaskInput =
  createAction<{ projectId: number; taskId: number; projectTaskInputCreateRequest: ProjectTaskInputCreateRequest }>("createProjectTaskInput");
export const updateProjectTaskInput =
  createAction<{ projectId: number; taskId: number; inputId: number; projectTaskInputCreateRequest: ProjectTaskInputCreateRequest }>("updateProjectTaskInput");
export const toggleProjectTaskInput =
  createAction<{ projectId: number; taskId: number; inputId: number; to_process: number }>("toggleProjectTaskInput");
export const deleteProjectTaskInput =
  createAction<{ projectId: number; taskId: number; inputId: number }>("deleteProjectTaskInput");
export const createProject =
  createAction<ProjectCreateRequest>("createProject");
export const updateProject =
  createAction<{ projectId: number; projectUpdateRequest: ProjectUpdateRequest }>("updateProject");
export const getAutomations = createAction("getAutomations");
export const getAutoLines = createAction<number>("getAutoLines");
export const updateAutoLine = createAction<{
  automationId: number;
  autoLineId: number;
  autoLineUpdateRequest: AutoLineUpdateRequest;
}>("updateAutoLine");
export const runAutomation = createAction<{
  automationId: number;
  selection: string;
  code: string;
}>("runAutomation");
export const getUsersInRole = createAction<number>("getUsersInRole");
export const linkRole =
  createAction<{roleId: number, userId: number}>("linkRole");
export const unlinkRole =
  createAction<{roleId: number, userId: number}>("unlinkRole");
export const linkDashToRole =
  createAction<{dashId: number, roleId: number}>("linkDashToRole");
export const linkFolderToRole =
  createAction<{folderId: number, roleId: number}>("linkFolderToRole");

// Repos
export const createRepo =
  createAction<RepoCreateRequest>("createRepo");
export const getRepos = createAction("getRepos");

// Scenarios
export const createScenario =
  createAction<ScenarioCreateRequest>("createScenario");
export const updateScenario =
  createAction<{ scenarioId: number } & ScenarioUpdateRequest>("updateScenario");
export const getScenarioMeasurables =
  createAction<number>("getScenarioMeasurables");
export const createScenarioMeasurable =
  createAction<{
    scenarioId: number;
    description: string;
    x: number;
    y: number;
    deps?: number[];
    dash_id?: number | null;
    priority?: number;
    user_guessed_probability?: number;
    for_whom?: string | null;
  }>("createScenarioMeasurable");
export const updateScenarioMeasurable =
  createAction<{
    scenarioId: number;
    measurableId: number;
    description: string;
    x: number;
    y: number;
    deps?: number[];
    dash_id?: number | null;
    priority?: number;
    user_guessed_probability?: number;
    for_whom?: string | null;
  }>("updateScenarioMeasurable");

export const pullRepo = 
  createAction<number>("pullRepo");
export const commitRepo = 
  createAction<{repoId: number, message: string}>("commitRepo");
export const pushRepo = 
  createAction<number>("pushRepo");

export const fileList = 
  createAction<{ id: number; folder: string; page_start?: number; page_size?: number; filter?: string }>("fileList");
export const codeSearch =
  createAction<{searchString: string, folder: string, repoId:number}>("codeSearch");
export const setFolderLabel =
  createAction<{repoId: number, folderPath: string, label: string}>("setFolderLabel");
export const getLineCounts =
  createAction<{repoId: number, files: string[]}>("getLineCounts");
export const mapFile =
  createAction<{repoId: number, filePath: string}>("mapFile");
export const getFunctionCallees =
  createAction<{repoId: number, methodId: number}>("getFunctionCallees");
export const getFeatureSlice =
  createAction<{repoId: number, methodId: number}>("getFeatureSlice");
export const getReverseSlice =
  createAction<{repoId: number, methodId: number}>("getReverseSlice");

// Dependency network chart
export const loadFileGraph =
  createAction<{repoId: number}>("loadFileGraph");
export const indexRepoBackground =
  createAction<{repoId: number, jobType?: "FULL" | "INCREMENTAL" | "PURGE", changedFiles?: string[]}>("indexRepoBackground");
export const pollIndexStatus =
  createAction<{repoId: number, untilDone?: boolean}>("pollIndexStatus");
export const expandNetworkFile =
  createAction<{repoId: number, fileId: number, filePath: string}>("expandNetworkFile");
export const expandNetworkFunction =
  createAction<{repoId: number, methodId: number}>("expandNetworkFunction");
export const expandNetworkCallers =
  createAction<{repoId: number, methodId: number}>("expandNetworkCallers");
export const expandNetworkSlice =
  createAction<{repoId: number, methodId: number}>("expandNetworkSlice");
export const showNetworkCode =
  createAction<{repoId: number, methodId?: number, filePath?: string, title: string, subtitle: string}>("showNetworkCode");
export const loadGraphSettings =
  createAction<{repoId: number}>("loadGraphSettings");
export const saveGraphSettings =
  createAction<{repoId: number, pov: "usage" | "functions" | "connections" | "altitude", hiddenDirs: string[], nodeColors: Record<string, string>}>("saveGraphSettings");
export const gitInfo = 
  createAction<{id: number, requestType: string, folder: string, count:number}>("gitInfo");
export const openFile = 
  createAction<{repoId: number, file_name: string}>("openFile");
export const deleteFile = 
  createAction<{repoId: number, file_name: string}>("deleteFile");
export const renameFile = 
  createAction<{repoId: number, file_name: string, new_repo_file_name: string}>("renameFile");
export const createRepoFile = 
  createAction<{repoId: number, file_name: string, file_path: string, initialCode?: string}>("createRepoFile");
export const createRepoFileWithContent = 
  createAction<{repoId: number, file_name: string, file_path: string, codeSet: string}>("createRepoFileWithContent");
export const createRepoFolder = 
  createAction<{repoId: number, folder_name: string, file_path: string}>("createRepoFolder");

// Repos
export const createBookmark =
  createAction<BookmarkCreateRequest>("createBookmark");

// Dashes
export const getDashes = createAction("getDashes");
export const createDash =
  createAction<DashCreateRequest>("createDash");
export const createDashAndAssignToMeasurable = createAction<{
  scenarioId: number;
  measurableId: number;
  description: string;
  x: number;
  y: number;
  deps?: number[];
  dashName?: string;
  terms?: string;
  /** Scenario's folder_id; dash is created in this folder. Falls back to current folder if null/undefined. */
  folder_id?: number | null;
  user_guessed_probability?: number;
  for_whom?: string | null;
}>("createDashAndAssignToMeasurable");
export const updateDash = createAction<Dash>(
  "updateDash"
);
export const updateDashFile = createAction<{dashId: number, currentCode:string }>(
  "updateDashFile"
);
export const getCodeProposals = createAction<{dashId: number}>(
  "getCodeProposals"
);
export const getCodeComments = createAction<{dashId: number}>(
  "getCodeComments"
);
export const assignCodeProposal = createAction<{dashId: number, proposalId: number, code: string}>(
  "assignCodeProposal"
);
export const previewDash = createAction<Dash>(
  "previewDash"
);
export const getDashImagePreview = createAction<Dash>(
  "getDashImagePreview"
);

export const deleteDash = createAction<Dash>(
  "deleteDash"
);
export const deleteFolder = createAction<Folder>(
  "deleteFolder"
);
export const cloneDash = createAction<{dash: Dash, target_workspace_id: number}>(
  "cloneDash"
);
export const moveDash = createAction<{dash: Dash, target_workspace_id: number}>(
  "moveDash"
);

export const undeleteDash = createAction<Dash>(
  "undeleteDash"
);


// Editor

export const setEditingDash = createAction<Dash>(
  "setEditingDash"
);
export const setEditingDataTable = createAction<number>(
  "setEditingDataTable"
);
export const createChart =
  createAction<ChartCreateRequest>("createChart");
export const createNavigation =
  createAction<NavigationCreateRequest>("createNavigation");
export const createApiCall =
  createAction<ApiCallCreateRequest>("createApiCall");
  export const updateApiCall =
createAction<ApiCallCreateRequest>("updateApiCall");
  export const executeApiCall =
  createAction<ApiCall>("executeApiCall");
export const createDataTable =
  createAction<DataTableCreateRequest>("createDataTable");
export const createDataTableColumn =
  createAction<DataTableCreateColumnRequest>("createDataTableColumn");
export const createDataTableRow =
  createAction<DataTableCreateRowRequest>("createDataTableRow");
export const createParameter =
  createAction<ParameterCreateRequest>("createParameter");
export const setPageNo = 
  createAction<number>("setPageNo");
export const attachChart = createAction<{
  sourceDocumentChartId: number;
  componentOrder: number;
}>("attachChart");
export const updateChart = createAction<Chart>("updateChart");
export const updateChartCode = createAction<{id: number, code: string}>("updateChartCode");
export const executeChartCode = createAction<{chart: Chart, code: string, parameters: string, folder: string}>("executeChartCode");
export const createChartAboveChart = createAction<{
  belowChart: Chart;
  insertedChart: ChartCreateRequest;
}>("createChartnAboveChart");
export const switchCharts = createAction<{
  sourceChart: Chart;
  destinationChart: Chart;
}>("switchChart");

// IDE / Browser state
export const saveIdeState = createAction("saveIdeState");
export const loadIdeState = createAction("loadIdeState");
export const deleteChart = createAction<Chart>(
  "deleteChartChart"
);

/*
export const getVariables = createAction("getVariables");
export const createVariable =
  createAction<VariableCreateRequest>("createVariable");
export const updateVariable = createAction<Variable>("updateVariable");
export const deleteVariable = createAction<Variable>("deleteVariable");
*/
